import { Typography, type TypographyProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface MultiCountDescriptor<T> {
  matchesItem: (item: T) => boolean;
  matchesSelectedId: (id: string | number) => boolean;
  name: string;
}

const countSelected = <T,>(
  descriptors: readonly MultiCountDescriptor<T>[],
  selectedIds: ReadonlyArray<string | number>,
): Map<string, number> => {
  const result = new Map<string, number>();

  for (const { matchesSelectedId, name } of descriptors) {
    for (const selectedId of selectedIds) {
      if (matchesSelectedId(selectedId)) result.set(name, (result.get(name) ?? 0) + 1);
    }
  }

  return result;
};

const countTotal = <T,>(
  descriptors: readonly MultiCountDescriptor<T>[],
  items: readonly T[],
): Map<string, number> => {
  const result = new Map<string, number>();

  for (const { matchesItem, name } of descriptors) {
    for (const item of items) {
      if (matchesItem(item)) result.set(name, (result.get(name) ?? 0) + 1);
    }
  }

  return result;
};

export interface MultiCountProps<T> extends Omit<TypographyProps, 'children'> {
  descriptors: readonly MultiCountDescriptor<T>[];
  items: readonly T[];
  selectedIds: ReadonlyArray<string | number>;
}

export const MultiCount = <T,>({
  descriptors,
  items,
  selectedIds,
  ...props
}: MultiCountProps<T>) => {
  const { i18n, t } = useTranslation();
  const selected = countSelected(descriptors, selectedIds);
  const total = countTotal(descriptors, items);

  const stringify = (things: Map<string, number>): string =>
    new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(
      [...things.entries()].map(([name, count]) => `${count}\xa0${t(name, { count })}`),
    );

  const text =
    selected.size > 0
      ? `${stringify(selected)} ${t('common.selected')}`
      : total.size > 0
      ? stringify(total)
      : '';

  return (
    <Typography component="p" variant="subtitle1" {...props}>
      {text}
    </Typography>
  );
};
